import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Loader2, RefreshCw, Send, UserRoundCheck, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useAppSelector } from '@/app/hooks';
import {
  fetchUserActivityUsers,
  fetchUserAnalyticsEvents,
  fetchUserChatLogs,
  adminSendMessage,
  adminSetHumanSupportMode,
  type AdminUserActivitySummary,
  type AdminUserAnalyticsEvent,
  type AdminUserChatLog,
} from '@/services/adminService';
import { logging } from '@/services/logging';
import { toast } from 'sonner';

type LoadStatus = 'idle' | 'loading' | 'error';

type UserTab = {
  id: string;
  participantType: 'registered' | 'temporary';
  userId: string | null;
  temporaryUserId: string | null;
  name: string | null;
  email: string | null;
};

type LoadState = {
  status: LoadStatus;
  error?: string | null;
};

const AUTO_REFRESH_OPTIONS = [
  { label: 'No', value: 'off', ms: 0 },
  { label: '2 sec', value: '2000', ms: 2_000 },
  { label: '5 sec', value: '5000', ms: 5_000 },
  { label: '15 sec', value: '15000', ms: 15_000 },
  { label: '30 sec', value: '30000', ms: 30_000 },
  { label: '1 min', value: '60000', ms: 60_000 },
] as const;

const formatTimestamp = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const toTimestamp = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.getTime();
};

const formatProperties = (properties: unknown): string[] => {
  if (!properties || typeof properties !== 'object') {
    return [];
  }
  return Object.entries(properties as Record<string, unknown>)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${String(value)}`);
};

const formatEventMeta = (event: AdminUserAnalyticsEvent): string[] => {
  const meta: string[] = [];
  if (event.screen) {
    meta.push(`screen: ${event.screen}`);
  }
  if (event.appName) {
    meta.push(`app: ${event.appName}`);
  }
  if (event.sessionId) {
    meta.push(`session: ${event.sessionId.slice(0, 8)}`);
  }
  const props = formatProperties(event.properties);
  if (props.length > 0) {
    meta.push(`props: ${props.join(', ')}`);
  }
  return meta;
};

const ROLE_LABELS: Record<AdminUserChatLog['role'], string> = {
  assistant: 'AI',
  user: 'US',
  interface: 'UI',
  supervisor: 'SPV',
  admin: 'You',
};

const getDisplayName = (participant: { name: string | null; email: string | null; id?: string }): string => {
  return participant.name ?? participant.email ?? participant.id ?? 'Guest user';
};

const parseSuggestedActions = (content: string): { message: string; actions: string[] } => {
  const marker = '\nSuggested actions: ';
  if (!content.includes(marker)) {
    return { message: content, actions: [] };
  }
  const [message, rest] = content.split(marker, 2);
  const actions = rest
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return { message, actions };
};

const ChatLogMessage = ({ log }: { log: AdminUserChatLog }) => {
  const isAdmin = log.role === 'admin';
  const label = ROLE_LABELS[log.role] ?? log.role;
  const parsed = parseSuggestedActions(log.content);
  return (
    <div className={`flex flex-col gap-2 p-4 ${isAdmin ? 'items-end' : 'items-start'}`}>
      <div className={`flex items-start gap-3 w-full ${isAdmin ? 'justify-end' : ''}`}>
        {!isAdmin && (
          <Avatar className="h-8 w-8">
            <AvatarFallback>{label}</AvatarFallback>
          </Avatar>
        )}
        <div
          className={`max-w-full rounded-lg px-3 py-2 text-sm sm:max-w-[75%] ${
            isAdmin ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
          }`}
        >
          <p className="whitespace-pre-wrap break-all">{parsed.message}</p>
        </div>
        {isAdmin && (
          <Avatar className="h-8 w-8">
            <AvatarFallback>{label}</AvatarFallback>
          </Avatar>
        )}
      </div>
      {parsed.actions.length > 0 && (
        <div className={`flex flex-wrap gap-2 ${isAdmin ? 'pr-11' : 'pl-11'}`}>
          {parsed.actions.map((action) => (
            <Button key={action} variant="outline" size="sm" disabled>
              {action}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminUserActivityPage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const [users, setUsers] = useState<AdminUserActivitySummary[]>([]);
  const [usersStatus, setUsersStatus] = useState<LoadStatus>('idle');
  const [usersError, setUsersError] = useState<string | null>(null);
  const [tabs, setTabs] = useState<UserTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [chatLogs, setChatLogs] = useState<Record<string, AdminUserChatLog[]>>({});
  const [eventLogs, setEventLogs] = useState<Record<string, AdminUserAnalyticsEvent[]>>({});
  const [chatState, setChatState] = useState<Record<string, LoadState>>({});
  const [eventState, setEventState] = useState<Record<string, LoadState>>({});
  const [chatRefreshInterval, setChatRefreshInterval] = useState('off');
  const [eventRefreshInterval, setEventRefreshInterval] = useState('off');
  const [cloudRefreshInterval, setCloudRefreshInterval] = useState('off');
  const [adminInput, setAdminInput] = useState<Record<string, string>>({});
  const [isSending, setIsSending] = useState<Record<string, boolean>>({});
  const [isUpdatingHumanMode, setIsUpdatingHumanMode] = useState<Record<string, boolean>>({});
  const [lastSeenSupportActivityByUserId, setLastSeenSupportActivityByUserId] = useState<Record<string, number>>({});
  const chatScrollAreaRef = useRef<HTMLDivElement | null>(null);
  const eventScrollAreaRef = useRef<HTMLDivElement | null>(null);

  const handleAdminSend = async (tab: UserTab) => {
    const content = adminInput[tab.id];
    if (!content?.trim()) return;

    try {
      setIsSending((prev) => ({ ...prev, [tab.id]: true }));
      await adminSendMessage(tab.userId, tab.temporaryUserId, content);
      setAdminInput((prev) => ({ ...prev, [tab.id]: '' }));
      await loadChatLogs(tab);
      toast.success('Message sent to user');
    } catch (error) {
      logging.error('Failed to send admin message', { error, tabId: tab.id });
      toast.error('Failed to send message');
    } finally {
      setIsSending((prev) => ({ ...prev, [tab.id]: false }));
    }
  };

  const handleHumanModeChange = async (tab: UserTab, enabled: boolean) => {
    try {
      setIsUpdatingHumanMode((prev) => ({ ...prev, [tab.id]: true }));
      await adminSetHumanSupportMode(tab.userId, tab.temporaryUserId, enabled);
      await loadChatLogs(tab);
      toast.success(enabled ? 'Human communication activated' : 'Human communication switched off');
    } catch (error) {
      logging.error('Failed to update human communication mode', { error, tabId: tab.id, enabled });
      toast.error('Failed to update human communication');
    } finally {
      setIsUpdatingHumanMode((prev) => ({ ...prev, [tab.id]: false }));
    }
  };

  const resolveRefreshMs = useCallback(
    (value: string) => AUTO_REFRESH_OPTIONS.find((option) => option.value === value)?.ms ?? 0,
    [],
  );

  const loadUsers = useCallback(async () => {
    try {
      setUsersStatus('loading');
      setUsersError(null);
      const result = await fetchUserActivityUsers();
      setUsers([...result]);
      setUsersStatus('idle');
    } catch (error) {
      logging.error('Failed to load user activity list', { error });
      setUsersStatus('error');
      setUsersError('Failed to load users.');
    }
  }, []);

  const loadChatLogs = useCallback(async (tab: UserTab) => {
    try {
      setChatState((prev) => ({ ...prev, [tab.id]: { status: 'loading', error: null } }));
      const result = await fetchUserChatLogs({ userId: tab.userId, temporaryUserId: tab.temporaryUserId });
      setChatLogs((prev) => ({ ...prev, [tab.id]: [...result].reverse() }));
      setChatState((prev) => ({ ...prev, [tab.id]: { status: 'idle', error: null } }));
    } catch (error) {
      logging.error('Failed to load user chat logs', { error, tabId: tab.id });
      setChatState((prev) => ({ ...prev, [tab.id]: { status: 'error', error: 'Failed to load chat.' } }));
    }
  }, []);

  const loadEventLogs = useCallback(async (tab: UserTab) => {
    try {
      setEventState((prev) => ({ ...prev, [tab.id]: { status: 'loading', error: null } }));
      const result = await fetchUserAnalyticsEvents({ userId: tab.userId, temporaryUserId: tab.temporaryUserId });
      setEventLogs((prev) => ({ ...prev, [tab.id]: [...result].reverse() }));
      setEventState((prev) => ({ ...prev, [tab.id]: { status: 'idle', error: null } }));
    } catch (error) {
      logging.error('Failed to load user analytics events', { error, tabId: tab.id });
      setEventState((prev) => ({ ...prev, [tab.id]: { status: 'error', error: 'Failed to load events.' } }));
    }
  }, []);

  const ensureUserData = useCallback(
    async (tabId: string, { force = false } = {}) => {
      const tab = tabs.find((item) => item.id === tabId);
      if (!tab) {
        return;
      }
      const hasChat = Object.prototype.hasOwnProperty.call(chatLogs, tabId);
      const hasEvents = Object.prototype.hasOwnProperty.call(eventLogs, tabId);
      if (force || !hasChat) {
        await loadChatLogs(tab);
      }
      if (force || !hasEvents) {
        await loadEventLogs(tab);
      }
    },
    [chatLogs, eventLogs, loadChatLogs, loadEventLogs, tabs]
  );

  useEffect(() => {
    if (isAdmin) {
      void loadUsers();
    }
  }, [isAdmin, loadUsers]);

  useEffect(() => {
    if (activeTabId) {
      void ensureUserData(activeTabId);
    }
  }, [activeTabId, ensureUserData]);

  useEffect(() => {
    if (!activeTabId) {
      return undefined;
    }
    const tab = tabs.find((item) => item.id === activeTabId);
    if (!tab) {
      return undefined;
    }
    const intervalMs = resolveRefreshMs(chatRefreshInterval);
    if (!intervalMs) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      void loadChatLogs(tab);
    }, intervalMs);
    return () => window.clearInterval(intervalId);
  }, [activeTabId, chatRefreshInterval, loadChatLogs, resolveRefreshMs, tabs]);

  useEffect(() => {
    if (!activeTabId) {
      return undefined;
    }
    const tab = tabs.find((item) => item.id === activeTabId);
    if (!tab) {
      return undefined;
    }
    const intervalMs = resolveRefreshMs(eventRefreshInterval);
    if (!intervalMs) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      void loadEventLogs(tab);
    }, intervalMs);
    return () => window.clearInterval(intervalId);
  }, [activeTabId, eventRefreshInterval, loadEventLogs, resolveRefreshMs, tabs]);

  const openUserTab = (userItem: AdminUserActivitySummary) => {
    const activityTime = toTimestamp(userItem.lastActivity) ?? Date.now();
    setLastSeenSupportActivityByUserId((prev) => ({ ...prev, [userItem.id]: activityTime }));
    setTabs((prev) => {
      if (prev.some((tab) => tab.id === userItem.id)) {
        return prev;
      }
      return [
        ...prev,
        {
          id: userItem.id,
          participantType: userItem.participantType,
          userId: userItem.userId,
          temporaryUserId: userItem.temporaryUserId,
          name: userItem.name,
          email: userItem.email,
        },
      ];
    });
    setActiveTabId(userItem.id);
  };

  const closeTab = (tabId: string) => {
    setTabs((prev) => {
      const nextTabs = prev.filter((tab) => tab.id !== tabId);
      setActiveTabId((current) => {
        if (current !== tabId) {
          return current;
        }
        return nextTabs.length > 0 ? nextTabs[nextTabs.length - 1].id : null;
      });
      return nextTabs;
    });
  };

  const activeUser = useMemo(() => tabs.find((tab) => tab.id === activeTabId) ?? null, [tabs, activeTabId]);
  const activeChatLogs = activeTabId ? chatLogs[activeTabId] ?? [] : [];
  const activeEventLogs = activeTabId ? eventLogs[activeTabId] ?? [] : [];
  const activeChatState: LoadState = activeTabId
    ? chatState[activeTabId] ?? { status: 'idle', error: null }
    : { status: 'idle', error: null };
  const activeEventState: LoadState = activeTabId
    ? eventState[activeTabId] ?? { status: 'idle', error: null }
    : { status: 'idle', error: null };
  const showChatLoading = activeChatState.status === 'loading' && activeChatLogs.length === 0;
  const showEventLoading = activeEventState.status === 'loading' && activeEventLogs.length === 0;
  const activeChatMode = activeChatLogs.find((log) => log.mode)?.mode ?? 'AI';

  useEffect(() => {
    const viewport = chatScrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement | null;
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [activeTabId, activeChatLogs]);

  useEffect(() => {
    const viewport = eventScrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement | null;
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [activeTabId, activeEventLogs]);

  const refreshActiveTab = () => {
    if (!activeTabId) {
      return;
    }
    void ensureUserData(activeTabId, { force: true });
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Admin access required</CardTitle>
            <CardDescription>You do not have permission to view this page.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/')}>Return home</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-theme(spacing.20))] p-4 md:p-6">
      <div className="grid h-full gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="flex min-h-0 flex-col">
          <CardHeader>
            <CardTitle>User activity</CardTitle>
            <CardDescription>Most recent analytics activity appears first.</CardDescription>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-0">
            <div className="px-4">
              <Button size="sm" variant="outline" onClick={loadUsers} disabled={usersStatus === 'loading'}>
                {usersStatus === 'loading' ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading
                  </span>
                ) : (
                  'Refresh users'
                )}
              </Button>
              {usersStatus === 'error' && (
                <p className="mt-2 text-xs text-destructive">{usersError}</p>
              )}
            </div>
            <ScrollArea className="flex-1 px-2 pb-4">
              <div className="space-y-2 px-2">
                {users.map((userItem) => {
                  const isActive = userItem.id === activeTabId;
                  const displayName = getDisplayName(userItem);
                  const lastSeenSupportActivity = lastSeenSupportActivityByUserId[userItem.id] ?? 0;
                  const currentUserActivity = toTimestamp(userItem.lastActivity) ?? 0;
                  const shouldShowSupportIndicator = userItem.hasHumanSupportRequest && currentUserActivity > lastSeenSupportActivity;
                  return (
                    <button
                      key={userItem.id}
                      type="button"
                      onClick={() => openUserTab(userItem)}
                      className={`w-full rounded-lg border p-3 text-left transition relative ${
                        isActive ? 'border-primary/60 bg-primary/10' : 'border-muted-foreground/20 hover:border-primary/40'
                      }`}
                    >
                      {shouldShowSupportIndicator && (
                        <div
                          className="absolute right-2 top-2 h-4 w-4 rounded-full border-2 border-white bg-red-500 shadow-sm animate-pulse"
                          title="New user message in human support mode"
                        />
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold">{displayName}</p>
                          {userItem.email && userItem.name && (
                            <p className="text-xs text-muted-foreground">{userItem.email}</p>
                          )}
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          {formatTimestamp(userItem.lastActivity)}
                        </span>
                      </div>
                    </button>
                  );
                })}
                {usersStatus === 'idle' && users.length === 0 && (
                  <p className="rounded-lg border border-dashed border-muted-foreground/40 p-4 text-center text-xs text-muted-foreground">
                    No users found.
                  </p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card className="flex min-h-0 flex-col">
          <CardHeader className="gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="mr-auto">User details</CardTitle>
              <Button size="sm" variant="outline" onClick={refreshActiveTab} disabled={!activeTabId}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tabs.length === 0 && (
                <span className="text-xs text-muted-foreground">Select a user to open a tab.</span>
              )}
              {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                return (
                  <div
                    key={tab.id}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                      isActive ? 'border-primary/60 bg-primary/10' : 'border-muted-foreground/30'
                    }`}
                  >
                    <button
                      type="button"
                      className="font-medium"
                      onClick={() => setActiveTabId(tab.id)}
                    >
                      {getDisplayName(tab)}
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => closeTab(tab.id)}
                      aria-label={`Close ${getDisplayName(tab)}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
            {activeUser && (
              <CardDescription>
                Viewing {getDisplayName(activeUser)}{' '}
                {activeUser.name && activeUser.email ? `(${activeUser.email})` : ''}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col">
            {!activeUser ? (
              <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 text-sm text-muted-foreground">
                Pick a user to see chat logs and activity.
              </div>
            ) : (
              <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-3">
                <div className="flex min-h-0 flex-col rounded-lg border">
                  <div className="border-b px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">Chat log</p>
                        <p className="text-xs text-muted-foreground">AIChatLog entries</p>
                      </div>
                      {activeUser && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={activeChatMode === 'HUMAN' ? 'default' : 'outline'}
                            onClick={() => void handleHumanModeChange(activeUser, true)}
                            disabled={isUpdatingHumanMode[activeUser.id]}
                          >
                            {isUpdatingHumanMode[activeUser.id] ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <UserRoundCheck className="mr-2 h-4 w-4" />
                            )}
                            Activate human
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={activeChatMode === 'AI' ? 'default' : 'outline'}
                            onClick={() => void handleHumanModeChange(activeUser, false)}
                            disabled={isUpdatingHumanMode[activeUser.id]}
                          >
                            {isUpdatingHumanMode[activeUser.id] ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Bot className="mr-2 h-4 w-4" />
                            )}
                            Switch off human
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Auto refresh</span>
                        <Select value={chatRefreshInterval} onValueChange={setChatRefreshInterval}>
                          <SelectTrigger className="h-8 w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AUTO_REFRESH_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <ScrollArea ref={chatScrollAreaRef} className="flex-1">
                    {showChatLoading ? (
                      <div className="flex h-full items-center justify-center py-6 text-sm text-muted-foreground">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading chat
                      </div>
                    ) : activeChatState.status === 'error' ? (
                      <div className="p-4 text-sm text-destructive">{activeChatState.error}</div>
                    ) : activeChatLogs.length === 0 ? (
                      <div className="p-4 text-sm text-muted-foreground">No chat messages yet.</div>
                    ) : (
                      <div className="space-y-2 pb-4">
                        {activeChatLogs.map((log) => (
                          <ChatLogMessage key={log.id} log={log} />
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  {activeUser && (
                    <div className="border-t p-3">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          void handleAdminSend(activeUser);
                        }}
                        className="flex items-center gap-2"
                      >
                        <Input
                          value={adminInput[activeUser.id] || ''}
                          onChange={(e) =>
                            setAdminInput((prev) => ({ ...prev, [activeUser.id]: e.target.value }))
                          }
                          placeholder="Type message to user..."
                          className="flex-1"
                          disabled={isSending[activeUser.id]}
                        />
                        <Button
                          type="submit"
                          size="icon"
                          disabled={!adminInput[activeUser.id]?.trim() || isSending[activeUser.id]}
                        >
                          {isSending[activeUser.id] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </form>
                    </div>
                  )}
                </div>

                <div className="flex min-h-0 flex-col rounded-lg border">
                  <div className="border-b px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">User activity</p>
                        <p className="text-xs text-muted-foreground">AnalyticsEvent entries</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Auto refresh</span>
                        <Select value={eventRefreshInterval} onValueChange={setEventRefreshInterval}>
                          <SelectTrigger className="h-8 w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AUTO_REFRESH_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <ScrollArea ref={eventScrollAreaRef} className="flex-1">
                    {showEventLoading ? (
                      <div className="flex h-full items-center justify-center py-6 text-sm text-muted-foreground">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading events
                      </div>
                    ) : activeEventState.status === 'error' ? (
                      <div className="p-4 text-sm text-destructive">{activeEventState.error}</div>
                    ) : activeEventLogs.length === 0 ? (
                      <div className="p-4 text-sm text-muted-foreground">No analytics events yet.</div>
                    ) : (
                      <div className="space-y-3 p-4">
                        {activeEventLogs.map((event) => {
                          const meta = formatEventMeta(event);
                          return (
                            <div key={event.id} className="rounded-lg border border-muted-foreground/20 p-3">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-semibold">{event.eventName}</p>
                                <span className="text-[11px] text-muted-foreground">
                                  {formatTimestamp(event.eventTimestamp)}
                                </span>
                              </div>
                              {meta.length > 0 ? (
                                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                                  {meta.map((line) => (
                                    <p key={line}>{line}</p>
                                  ))}
                                </div>
                              ) : (
                                <p className="mt-2 text-xs text-muted-foreground">No metadata.</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </div>

                <div className="flex min-h-0 flex-col rounded-lg border">
                  <div className="border-b px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">Cloud logs</p>
                        <p className="text-xs text-muted-foreground">Google Cloud logs per user</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Auto refresh</span>
                        <Select value={cloudRefreshInterval} onValueChange={setCloudRefreshInterval}>
                          <SelectTrigger className="h-8 w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {AUTO_REFRESH_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-1 items-center justify-center px-4 py-6 text-sm text-muted-foreground">
                    Coming soon. We will wire Google Cloud logs in the next stage.
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUserActivityPage;
